import { useEffect, useState } from "react";
import { IGetBookingForm, WasteItem } from "../../core/types/types";
import flavourConfig from "../../environment/flavourConfig";
import { useHistory } from "react-router-dom";
import { apiService } from "../../core/services/apiService";

const UpdateForm: React.FC<IGetBookingForm> = ({
  updateFormId,
  customerTicketNumber,
  updateCustomerTicketNumber,
  customer,
  updateCustomer,
  updateExistingBooking,
  updateHasCompletedCustomerPage,
  updateIsLoading,
  updateDetailsError,
  updateIsSubmitButtonDisabled,
}: IGetBookingForm) => {
  const history = useHistory();
  const detailsFormId = "details-update";

  const [ticketNumberInput, updateTicketNumberInput] = useState("");

  // Set 'Next' button to use this form id
  useEffect(() => {
    updateFormId(detailsFormId);
    updateTicketNumberInput(customerTicketNumber.toString());
  }, [updateFormId, updateTicketNumberInput, customerTicketNumber]);

  useEffect(() => {
    updateIsSubmitButtonDisabled(!customer.CustomerEmail || !ticketNumberInput);
  }, [customer.CustomerEmail, updateIsSubmitButtonDisabled, ticketNumberInput]);

  const handleTicketNumberChange = (value: string) => {
    updateTicketNumberInput(value);

    if (value) {
      const parsed = parseInt(value);
      if (!isNaN(parsed)) {
        updateCustomerTicketNumber(parsed);
      }
    }
  };

  // Main submit method
  const handleSubmit = async () => {
    updateIsLoading(true);
    updateDetailsError(null);
    const customerResponse = await apiService.getCustomerByEmail(
      customer.CustomerEmail,
      customerTicketNumber
    );
    let error =
      "We were unable to make this request. Please try again later or contact us on 0800 246 978.";

    // Customer exists
    if (customerResponse?.status === 200) {
      const customerLinesResponse = await apiService.getCustomerLines(
        customerTicketNumber
      );

      if (customerLinesResponse?.status === 200) {
        const wasteItemList: WasteItem[] = customerLinesResponse.data.map(
          ({ id, ...wasteItem }: { id: number; wasteItem: WasteItem }) =>
            wasteItem
        );

        updateExistingBooking(wasteItemList);
        updateIsLoading(false);
        updateHasCompletedCustomerPage(true);
        history.push("/waste-form");
        return;
      } else if (customerLinesResponse?.status !== 200) {
        error =
          "There was an issue getting your booking information. Please try again later or contact us on 0800 246 978.";
      }
    } else if (customerResponse?.status === 204) {
      error = "Your email or ticket number is incorrect. Please try again.";
    }

    updateIsLoading(false);
    updateDetailsError(error);
  };

  return (
    <>
      <form
        id={detailsFormId}
        className="w-full"
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        <div className="mb-4 xl:mb-6 w-full flex flex-col items-start justify-center">
          <h1 className="body-heading-font">Enter Your Details</h1>
          <p className="body-subheading-font mt-2">
            Please fill in the form below with your details to get your booking
            information. You can click on the information icon in the top right
            corner if you need help.
          </p>
        </div>
        <div className="w-full flex flex-col items-end">
          <div className="w-full md:flex flex-wrap gap-10">
            <div className="xl:w-2/5 lg:w-2/5 md:w-2/5 flex flex-col mb-4 xl:mb-6">
              <label
                htmlFor="email2"
                className="pb-1.5 xl:pb-2 form-subheading-font"
              >
                Email Address<span className="text-red-600">*</span>
              </label>
              <div className="relative">
                <div className="absolute text-gray-600 flex items-center px-4 border-r h-full">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="icon icon-tabler icon-tabler-mail"
                    width={20}
                    height={20}
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path stroke="none" d="M0 0h24v24H0z" />
                    <rect x={3} y={5} width={18} height={14} rx={2} />
                    <polyline points="3 7 12 13 21 7" />
                  </svg>
                </div>
                <input
                  id="email2"
                  name="email"
                  type="email"
                  value={customer.CustomerEmail}
                  required
                  className={`w-full body-input-font bg-transparent border-gray-300 focus:outline-none focus:border-${flavourConfig.inputColor} font-normal py-3 flex items-center pl-16 rounded border shadow-sm`}
                  onChange={(e) => {
                    updateCustomer({
                      ...customer,
                      CustomerEmail: e.target.value,
                    });
                  }}
                />
              </div>
            </div>
            <div className="xl:w-1/5 lg:w-1/5 md:w-2/5 flex flex-col mb-4 xl:mb-6">
              <label
                htmlFor="Ticket Number"
                className="pb-1.5 xl:pb-2 form-subheading-font"
              >
                Ticket Number<span className="text-red-600">*</span>
              </label>
              <input
                type="number"
                name="Ticket Number"
                value={ticketNumberInput}
                required
                id="Ticket Number"
                className={`body-input-font border border-gray-300 pl-3 py-3 shadow-sm rounded focus:outline-none bg-transparent focus:border-${flavourConfig.inputColor}`}
                onChange={(e) => handleTicketNumberChange(e.target.value)}
              />
            </div>
          </div>
        </div>
      </form>
    </>
  );
};

export default UpdateForm;
